import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  FileText, 
  Download,
  CalendarBlank,
  Package,
  ArrowsClockwise,
  Cube,
  CurrencyDollar
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState('orders');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date()
  });
  const [filters, setFilters] = useState({
    status: '',
    warehouse_id: '',
    return_type: ''
  });
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [reportType, dateRange]);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/warehouses`, { withCredentials: true });
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('start_date', dateRange.from.toISOString().split('T')[0]);
      if (dateRange.to) params.append('end_date', dateRange.to.toISOString().split('T')[0]);
      if (filters.status) params.append('status', filters.status);
      if (filters.warehouse_id) params.append('warehouse_id', filters.warehouse_id);
      if (filters.return_type) params.append('return_type', filters.return_type);
      
      const response = await axios.get(`${API_URL}/api/reports/${reportType}?${params}`, { withCredentials: true });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;
    
    // Convert to CSV
    let csv = '';
    const data = reportData[reportType] || reportData.transactions || reportData.inventory || [];
    
    if (data.length > 0) {
      const headers = Object.keys(data[0]);
      csv += headers.join(',') + '\n';
      data.forEach(row => {
        csv += headers.map(h => JSON.stringify(row[h] || '')).join(',') + '\n';
      });
    }
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <DashboardLayout 
      title="Reports" 
      subtitle="Generate and export business reports"
      actions={
        <Button 
          onClick={handleExport}
          disabled={!reportData}
          variant="outline"
          data-testid="export-report-btn"
        >
          <Download size={18} className="mr-2" />
          Export CSV
        </Button>
      }
    >
      {/* Report Type Tabs */}
      <Tabs value={reportType} onValueChange={setReportType} className="space-y-4">
        <div className="bg-white border border-[#e5e5ea] p-4 space-y-4">
          <TabsList className="bg-[#fcfcfc] p-1">
            <TabsTrigger value="orders" className="gap-2" data-testid="orders-report-tab">
              <Package size={16} /> Orders
            </TabsTrigger>
            <TabsTrigger value="returns" className="gap-2" data-testid="returns-report-tab">
              <ArrowsClockwise size={16} /> Returns
            </TabsTrigger>
            <TabsTrigger value="inventory" className="gap-2" data-testid="inventory-report-tab">
              <Cube size={16} /> Inventory
            </TabsTrigger>
            <TabsTrigger value="settlement" className="gap-2" data-testid="settlement-report-tab">
              <CurrencyDollar size={16} /> Settlement
            </TabsTrigger>
          </TabsList>

          {/* Date Range Picker */}
          <div className="flex gap-4 flex-wrap">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="date-range-picker">
                  <CalendarBlank size={16} />
                  {dateRange.from ? format(dateRange.from, 'MMM d, yyyy') : 'Start Date'} 
                  {' - '}
                  {dateRange.to ? format(dateRange.to, 'MMM d, yyyy') : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.from, to: dateRange.to }}
                  onSelect={(range) => setDateRange({ from: range?.from, to: range?.to || range?.from })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {reportType === 'orders' && (
              <Select value={filters.status || "all"} onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}>
                <SelectTrigger className="w-40 rounded-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            )}

            {reportType === 'inventory' && (
              <Select value={filters.warehouse_id || "all"} onValueChange={(value) => setFilters({ ...filters, warehouse_id: value === "all" ? "" : value })}>
                <SelectTrigger className="w-48 rounded-sm">
                  <SelectValue placeholder="Warehouse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouses</SelectItem>
                  {warehouses.map(wh => (
                    <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {reportType === 'returns' && (
              <Select value={filters.return_type || "all"} onValueChange={(value) => setFilters({ ...filters, return_type: value === "all" ? "" : value })}>
                <SelectTrigger className="w-44 rounded-sm">
                  <SelectValue placeholder="Return Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="delivered_return">Delivered Return</SelectItem>
                  <SelectItem value="rto">RTO</SelectItem>
                </SelectContent>
              </Select>
            )}

            <Button onClick={fetchReport} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="generate-report-btn">
              Generate Report
            </Button>
          </div>
        </div>

        {/* Report Content */}
        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-20 skeleton rounded-xl" />)}</div>
            <div className="h-64 skeleton rounded-xl" />
          </div>
        ) : reportData ? (
          <>
            <TabsContent value="orders">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Orders</p>
                  <p className="text-2xl font-bold mt-1">{reportData.total_orders || 0}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Amount</p>
                  <p className="text-2xl font-bold mt-1 text-[#28cd8c]">{formatCurrency(reportData.total_amount)}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Status Breakdown</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(reportData.status_breakdown || {}).map(([status, count]) => (
                      <span key={status} className="text-xs px-2 py-1 bg-[#fcfcfc] rounded">
                        {status}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="returns">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Returns</p>
                  <p className="text-2xl font-bold mt-1">{reportData.total_returns || 0}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">QC Passed</p>
                  <p className="text-2xl font-bold mt-1 text-[#28cd8c]">{reportData.qc_passed || 0}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">QC Failed</p>
                  <p className="text-2xl font-bold mt-1 text-[#ea4b5a]">{reportData.qc_failed || 0}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="inventory">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Items</p>
                  <p className="text-2xl font-bold mt-1">{reportData.total_items || 0}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Quantity</p>
                  <p className="text-2xl font-bold mt-1">{reportData.total_quantity || 0}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Low Stock</p>
                  <p className="text-2xl font-bold mt-1 text-[#d18f08]">{reportData.low_stock_count || 0}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Out of Stock</p>
                  <p className="text-2xl font-bold mt-1 text-[#ea4b5a]">{reportData.zero_stock_count || 0}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="settlement">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Credits</p>
                  <p className="text-2xl font-bold mt-1 text-[#28cd8c]">{formatCurrency(reportData.total_credits)}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Total Debits</p>
                  <p className="text-2xl font-bold mt-1 text-[#ea4b5a]">{formatCurrency(reportData.total_debits)}</p>
                </div>
                <div className="bg-white border border-[#e5e5ea] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Net Amount</p>
                  <p className={`text-2xl font-bold mt-1 ${reportData.net_amount >= 0 ? 'text-[#28cd8c]' : 'text-[#ea4b5a]'}`}>
                    {formatCurrency(reportData.net_amount)}
                  </p>
                </div>
              </div>
            </TabsContent>
          </>
        ) : (
          <div className="bg-white border border-[#e5e5ea] p-12 text-center">
            <FileText size={64} className="mx-auto text-[#e5e5ea] mb-4" />
            <h4 className="font-bold text-xl text-[#0A0A0A] mb-2">Generate a Report</h4>
            <p className="text-[#5a5c87]">Select report type and date range to view data</p>
          </div>
        )}
      </Tabs>
    </DashboardLayout>
  );
}
